// src/context/ConversationContext.js
// Holds the chat-history sidebar state (conversation list + current conversation)
// and brokers persistence to MongoDB via chat-history-api. landingpage registers
// two handlers so the context can reset / reload the chat view.
import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { UserAuth } from './AuthContext';
import {
  listConversations,
  getConversation,
  createConversation,
  renameConversation as apiRename,
  deleteConversation as apiDelete,
  appendMessage,
  generateTitle
} from '../utils/chat-history-api';

const ConversationContext = createContext(null);

export const useConversations = () => useContext(ConversationContext);

export const ConversationProvider = ({ children }) => {
  const { user } = UserAuth() || {};
  const [conversations, setConversations] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const currentIdRef = useRef(null);
  const pendingIdRef = useRef(null); // Promise of an in-flight conversation creation

  // landingpage registers these so the sidebar can drive the chat view.
  const handlersRef = useRef({ resetChat: () => {}, loadConversation: () => {} });
  const registerChatHandlers = useCallback((handlers) => {
    handlersRef.current = { ...handlersRef.current, ...handlers };
  }, []);

  const setCurrent = useCallback((id) => {
    currentIdRef.current = id;
    setCurrentId(id);
  }, []);

  const refreshList = useCallback(async () => {
    if (!user) return;
    setLoadingList(true);
    try {
      const list = await listConversations();
      setConversations(Array.isArray(list) ? list : []);
    } catch (err) {
      console.warn('[conversations] refresh failed:', err.message);
    } finally {
      setLoadingList(false);
    }
  }, [user]);

  // Load the user's conversations on sign-in; clear on sign-out.
  useEffect(() => {
    if (user) {
      refreshList();
    } else {
      setConversations([]);
      setCurrent(null);
    }
  }, [user, refreshList, setCurrent]);

  // Start a brand-new chat: reset the view; the next query creates the conversation.
  const newChat = useCallback(() => {
    try { handlersRef.current.resetChat?.(); } catch (e) { /* noop */ }
    setCurrent(null);
  }, [setCurrent]);

  // Called by landingpage when a query is submitted. Creates the conversation on the
  // first query and persists the user message. Returns a promise of the id, which is
  // also stashed in pendingIdRef so the assistant write can await it (no lost replies).
  const handleUserQuery = useCallback((queryText) => {
    if (!user) return Promise.resolve(null); // history only for signed-in users

    const run = (async () => {
      try {
        let id = currentIdRef.current;
        let isNew = false;
        if (!id) {
          const stub = await createConversation(queryText);
          id = stub._id;
          isNew = true;
          setCurrent(id);
          setConversations((prev) => [{ ...stub }, ...prev.filter((c) => c._id !== id)]);
        }
        const res = await appendMessage(id, 'user', queryText);
        // Bump this conversation to the top with its (interim) title.
        setConversations((prev) => {
          const others = prev.filter((c) => c._id !== id);
          const existing = prev.find((c) => c._id === id) || {};
          return [
            { ...existing, _id: id, title: res?.title || existing.title || queryText, lastMessageAt: res?.lastMessageAt || new Date().toISOString() },
            ...others
          ];
        });
        // Claude-style: replace the interim title with an LLM-generated short one.
        if (isNew) {
          generateTitle(id, queryText)
            .then((r) => {
              if (r?.title) {
                setConversations((prev) => prev.map((c) => (c._id === id ? { ...c, title: r.title } : c)));
              }
            })
            .catch(() => { /* keep interim title */ });
        }
        return id;
      } catch (err) {
        console.warn('[conversations] handleUserQuery failed:', err.message);
        return null;
      }
    })();

    pendingIdRef.current = run;
    return run;
  }, [user, setCurrent]);

  // Called by landingpage after the assistant response completes. Awaits any in-flight
  // conversation creation so the reply always attaches to the right chat.
  const handleAssistantResponse = useCallback(async (text, payload, conversationIdOverride = null) => {
    if (!user) return;
    try {
      let id = conversationIdOverride || currentIdRef.current;
      if (!id && pendingIdRef.current) id = await pendingIdRef.current;
      if (!id) return;
      const res = await appendMessage(id, 'assistant', text || '', payload);
      setConversations((prev) => {
        const others = prev.filter((c) => c._id !== id);
        const existing = prev.find((c) => c._id === id) || { _id: id };
        return [{ ...existing, lastMessageAt: res?.lastMessageAt || new Date().toISOString() }, ...others];
      });
    } catch (err) {
      console.warn('[conversations] handleAssistantResponse failed:', err.message);
    }
  }, [user]);

  // Open a past conversation: fetch it and hand its messages to the chat view.
  const selectConversation = useCallback(async (id) => {
    if (!user) return;
    try {
      const convo = await getConversation(id);
      setCurrent(id);
      try { handlersRef.current.loadConversation?.(convo); } catch (e) { /* noop */ }
    } catch (err) {
      console.warn('[conversations] selectConversation failed:', err.message);
    }
  }, [user, setCurrent]);

  const renameConversation = useCallback(async (id, title) => {
    try {
      const stub = await apiRename(id, title);
      setConversations((prev) => prev.map((c) => (c._id === id ? { ...c, title: stub?.title || title } : c)));
    } catch (err) {
      console.warn('[conversations] rename failed:', err.message);
    }
  }, []);

  const deleteConversation = useCallback(async (id) => {
    try {
      await apiDelete(id);
      setConversations((prev) => prev.filter((c) => c._id !== id));
      if (currentIdRef.current === id) newChat();
    } catch (err) {
      console.warn('[conversations] delete failed:', err.message);
    }
  }, [newChat]);

  return (
    <ConversationContext.Provider
      value={{
        conversations,
        currentId,
        loadingList,
        refreshList,
        newChat,
        handleUserQuery,
        handleAssistantResponse,
        selectConversation,
        renameConversation,
        deleteConversation,
        registerChatHandlers
      }}
    >
      {children}
    </ConversationContext.Provider>
  );
};

export default ConversationContext;
